'use client';
import { useFormatter, useTranslations } from 'next-intl';
import { type FC, useEffect, useRef, useState } from 'react';
import { BaseLineInfo } from '@/app/components/icons/BaseLineInfo';
import { ChannelApple } from '@/app/components/icons/ChannelApple';
import { ChannelGoogle } from '@/app/components/icons/ChannelGoogle';
import { ChannelStripe } from '@/app/components/icons/ChannelStripe';
import { ChannelZfb } from '@/app/components/icons/ChannelZfb';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { UserAvatar } from '@/app/components/shared/UserAvatar';
import { Button } from '@/app/components/ui/button';
import { useFetch, useGotoManageStripe } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { SubsPlanType } from '@/app/types';
import { cn, formatExpiredDate, formatPrice, reportEvent } from '@/app/utils';
import { subscriptionsGetMyExpiredSubscription } from '../apis';
import { PAY_CHANNEL_MAP, PLAN_TYPE_MAP } from '../constants';

interface HeaderProps {
  setOpenOrderList: (open: boolean) => void;
}

export const Header: FC<HeaderProps> = ({ setOpenOrderList }) => {
  const t = useTranslations();
  const format = useFormatter();

  const {
    userInfo,
    subscription,
    activeTeamBundleProduct,
    creditsInfo,
    nextPlan,
    presentPlanEffectiveTime,
    isGiftPlan,
    isTeamSpace,
    teamSpaceInfo,
  } = useStore((state) => ({
    userInfo: state.userInfo,
    subscription: state.subscription,
    activeTeamBundleProduct: state.activeTeamBundleProduct,
    creditsInfo: state.creditsInfo,
    nextPlan: state.nextPlan,
    presentPlanEffectiveTime: state.presentPlanEffectiveTime,
    isGiftPlan: state.isGiftPlan,
    isTeamSpace: state.isTeamSpace,
    teamSpaceInfo: state.teamSpaceInfo,
  }));

  // 「我的订单」入口：个人用户可见；团队空间下仅管理员可见，普通成员隐藏
  const canViewOrder = !isTeamSpace || teamSpaceInfo?.role === 'admin';

  const { loading: manageLoading, handleGotoManageStripe } = useGotoManageStripe();

  const containerRef = useRef<HTMLDivElement>(null);
  const [subscriptionExpiredDiffDays, setSubscriptionExpiredDiffDays] = useState<number | null>(null);

  const plan_type = (userInfo?.subs_plan as SubsPlanType) ?? 'free';
  const sub_plan_type = subscription?.plan_type ?? 'free';
  const pay_channel = subscription?.pay_channel;
  const subscribedCredits = creditsInfo?.subscribed || 0;

  const { run: getExpiredSubscription } = useFetch(subscriptionsGetMyExpiredSubscription, {
    manual: true,
    onSuccess: ({ data }) => {
      const expiredSubscriptionTime = data?.subscription?.end_time;
      if (!expiredSubscriptionTime) return;
      const now = new Date();
      const expiredDate = new Date(expiredSubscriptionTime);
      const diffTime = Math.abs(expiredDate.getTime() - now.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      setSubscriptionExpiredDiffDays(diffDays <= 365 ? diffDays : null);
    },
  });

  useEffect(() => {
    if (plan_type === 'free' && !subscription) {
      getExpiredSubscription();
    }
  }, [plan_type, subscription, presentPlanEffectiveTime]);

  const formatBillingDate = (time?: string) =>
    format.dateTime(new Date(time || ''), { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone });

  const getPeriodLabel = (billing_period?: string, product_type?: string) => {
    if (billing_period === 'monthly' && product_type === 'subscription')
      return t('payments.current.subscriptionMonthly');
    if (billing_period === 'yearly' && product_type === 'subscription') return t('payments.current.subscriptionYearly');
    if (billing_period === 'monthly' && (product_type === 'pass' || product_type === 'claw_pass_bundle'))
      return t('payments.current.passMonthly');
    if (billing_period === 'yearly' && (product_type === 'pass' || product_type === 'claw_pass_bundle'))
      return t('payments.current.passYearly');
    return '';
  };

  const getPaymentTimeTitle = (): string => {
    if (!subscription) {
      return subscriptionExpiredDiffDays ? t('payments.bill.expiredDays', { days: subscriptionExpiredDiffDays }) : '';
    }
    if (subscription.status === 'canceled' && subscription.end_time) {
      return t('payments.bill.cancel', { date: formatExpiredDate(subscription.end_time, format) });
    }
    if (nextPlan?.next_plan_type) {
      return t('payments.bill.nextPlan', {
        plan: t(`payments.${nextPlan.next_plan_type}`),
        date: formatBillingDate(subscription.next_billing_time),
      });
    }
    if (subscription.product_type === 'subscription') {
      const billingDate = formatBillingDate(subscription.next_billing_time);
      const amount = formatPrice(subscription.price);
      return subscription.from_change_type === 'end_of_period'
        ? t('payments.bill.nextPaymentFromChange', { plan: t(`payments.${sub_plan_type}`), date: billingDate, amount })
        : t('payments.bill.nextPayment', { date: billingDate, amount });
    }
    if (subscription.product_type === 'pass') {
      return t('payments.bill.expired', { date: formatExpiredDate(subscription.period_end, format) });
    }
    return '';
  };

  if (!userInfo) return null;

  const { nickname, phone, email } = userInfo;
  // 团队空间下用 active_team_bundle_product.tier 推导具体档位（基础版/Plus/Pro），优先于通用 plan_type 文案
  const teamTier = activeTeamBundleProduct?.tier;
  const teamTierLabel = teamTier
    ? teamTier === 'unspecified'
      ? t('payments.team_v2')
      : t(`payments.plans.tier${teamTier.charAt(0).toUpperCase()}${teamTier.slice(1)}` as 'payments.plans.tierBasic')
    : t('payments.free');
  const currentPlanLabel = isTeamSpace ? teamTierLabel : t(`payments.${plan_type}`);
  const periodLabel = isTeamSpace ? '' : getPeriodLabel(subscription?.billing_period, subscription?.product_type);
  const paymentTimeTitle = getPaymentTimeTitle();
  const channelLabel = pay_channel
    ? pay_channel === 'alipay'
      ? '您当时使用支付宝订阅，可以前往支付宝管理订阅'
      : t('payments.current.via', { pay_channel: PAY_CHANNEL_MAP[pay_channel] })
    : '';

  const giftCreditsExpiredLine =
    subscribedCredits > 0 && creditsInfo?.credit_sub_expires_at
      ? t('payments.current.creditsExpired', {
          credits: subscribedCredits,
          date: format.dateTime(new Date(creditsInfo.credit_sub_expires_at), {
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          }),
        })
      : '';

  const giftPlanLine = isGiftPlan
    ? presentPlanEffectiveTime.length > 0
      ? t('payments.current.presentPlanExpired', {
          dates: `${format.dateTime(new Date(presentPlanEffectiveTime[0] || ''), {
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          })} - ${format.dateTime(new Date(presentPlanEffectiveTime[1] || ''), {
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          })}`,
        })
      : t('payments.current.presentPlanExpiredForever')
    : '';

  const hasEntitlementInfo = Boolean(giftCreditsExpiredLine || giftPlanLine);
  const entitlementTooltip = hasEntitlementInfo && (
    <div className="flex flex-col gap-2 text-xs">
      {giftCreditsExpiredLine && <div>{giftCreditsExpiredLine}</div>}
      {giftPlanLine && <div>{giftPlanLine}</div>}
    </div>
  );

  const planTooltip = paymentTimeTitle && <div className="text-xs">{paymentTimeTitle}</div>;

  const handleOpenOrderList = () => {
    setOpenOrderList(true);
    reportEvent('element_click', {
      from_page: 'my-subscription',
      element_name: 'order_button',
    });
  };

  return (
    <div
      ref={containerRef}
      className="flex items-center justify-between gap-6 px-4 py-6 sm:flex-col sm:items-stretch sm:gap-6"
    >
      <div className="flex flex-1 items-center gap-12 md:flex-col md:items-stretch md:gap-4">
        <div className="flex min-w-0 items-center gap-3">
          <UserAvatar userInfo={userInfo} className="size-10 shrink-0" />
          <div className="flex min-w-0 flex-col gap-1">
            <div className="max-w-[200px] truncate font-medium text-base">{nickname}</div>
            <div className="truncate text-system-white40 text-xs">{phone || email || ''}</div>
          </div>
        </div>

        <div className="h-10 w-px shrink-0 bg-system-white08 md:h-px md:w-full" />

        <div className="flex flex-1 items-center gap-10 sm:gap-4">
          <div className="flex min-w-0 flex-col items-start">
            <div className="font-semibold text-white text-xl sm:text-base">{creditsInfo?.total ?? 0}</div>
            <div className="text-system-white40 text-xs">{t('payments.current.currentCredits')}</div>
          </div>

          <div className="h-10 w-px shrink-0 bg-system-white08" />

          <div className="flex min-w-0 flex-col items-start">
            <div className="flex items-center gap-2">
              <span className={cn('bg-clip-text font-semibold text-xl sm:text-base', PLAN_TYPE_MAP[plan_type])}>
                {currentPlanLabel}
                {isGiftPlan ||
                  (isTeamSpace && teamTier === 'unspecified' && ` ${t('payments.current.presentPlanDesc')}`)}
              </span>
              {entitlementTooltip && (
                <CustomTooltip content={entitlementTooltip} trigger="auto" container={containerRef.current}>
                  <BaseLineInfo className="size-4 shrink-0 cursor-pointer text-system-white36 transition-colors hover:text-system-white" />
                </CustomTooltip>
              )}
            </div>
            <div className="text-system-white40 text-xs">{t('payments.current.currentSubscription')}</div>
          </div>

          {!isTeamSpace && (
            <>
              <div className="h-10 w-px shrink-0 bg-system-white08" />

              <div className="flex min-w-0 flex-col items-start">
                <div className="flex items-center gap-2 font-semibold text-base">
                  <span className={cn('bg-clip-text text-xl sm:text-base', PLAN_TYPE_MAP[sub_plan_type])}>
                    {t(`payments.${sub_plan_type}`)}
                  </span>
                  {periodLabel && <span className="text-white">{periodLabel}</span>}
                  {pay_channel === 'stripe' ? (
                    <Button
                      disabled={manageLoading}
                      variant="outline"
                      className="h-5 min-w-12 gap-1 px-1 text-xs"
                      onClick={() => {
                        reportEvent('element_click', {
                          from_page: 'my-subscription',
                          element_name: 'manage_button',
                        });
                        handleGotoManageStripe();
                      }}
                    >
                      <ChannelStripe className="size-3.5" />
                      {t('payments.current.manageSubscription')}
                    </Button>
                  ) : channelLabel ? (
                    <CustomTooltip
                      content={channelLabel}
                      trigger="auto"
                      disableHoverableContent
                      container={containerRef.current}
                    >
                      <span className="inline-flex items-center">
                        {pay_channel === 'apple' && <ChannelApple className="size-4 cursor-pointer" />}
                        {pay_channel === 'google' && <ChannelGoogle className="size-4 cursor-pointer" />}
                        {pay_channel === 'alipay' && <ChannelZfb className="size-4 cursor-pointer" />}
                      </span>
                    </CustomTooltip>
                  ) : null}
                  {planTooltip && (
                    <CustomTooltip content={planTooltip} trigger="auto" container={containerRef.current}>
                      <BaseLineInfo className="size-4 shrink-0 cursor-pointer text-system-white36 transition-colors hover:text-system-white" />
                    </CustomTooltip>
                  )}
                </div>
                <div className="text-system-white40 text-xs">{t('payments.current.currentPlan')}</div>
              </div>
            </>
          )}
        </div>
      </div>

      {canViewOrder && (
        <div className="flex shrink-0 items-center gap-4 sm:w-full sm:gap-2">
          <Button
            variant="default"
            className="h-11 rounded-[12px] border border-system-white08 bg-system-white16 px-8 font-normal text-sm text-system-white hover:bg-system-white20 sm:h-9 sm:flex-1 sm:px-4"
            onClick={handleOpenOrderList}
          >
            {t('payments.order')}
          </Button>
        </div>
      )}
    </div>
  );
};
